import {
  getWeekOfMonth,
  getWeeksInMonth,
  lastDayOfMonth,
  lastDayOfYear,
  startOfWeek,
} from 'date-fns';
import { Master } from 'little-state-machine';
import React from 'react';

import { TypeUtils } from '../../framework/utils/UtilityTypes';
import DateChanger, {
  DateChangerProps,
  DateChangerStateBase,
} from '../components/DateChanger';

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

/**
 * 年月切替状態
 */
export type MonthChangerState = DateChangerStateBase & {
  /**
   * 月
   */
  month: number;
};

/**
 * 日切替状態
 */
export type DayChangerState = MonthChangerState & {
  /**
   * 日
   */
  day: number;
};

/**
 * 半月切替状態
 */
export type HalfMonthChangerState = MonthChangerState & {
  /**
   * 前後半
   */
  half: number;
};

/**
 * 週切替状態
 */
export type WeekChangerState = MonthChangerState & {
  /**
   * 週
   */
  week: number;
};

/**
 * 年切替状態
 */
export type YearChangerState = DateChangerStateBase;

export type DateChangerState = DateChangerStateBase & {
  month: number;
  half: number;
  week: number;
  day: number;
};

/**
 * 日付切替コンポーネント設定ベース
 */
type DateChangerSetting = {
  /**
   * 初期状態
   */
  initialState?: DateChangerState;
  /**
   * 左端
   */
  leftDeadend?: DateChangerState;
  /**
   * 右端
   */
  rightDeadend?: DateChangerState;
  onChangeMonth?: Consumer<DateChangerState>;
  applyUnits?: { 1?: Master; 2?: Master; 0?: Master };
};

// ----------------------------------------------------------------------------
// Methods
// ----------------------------------------------------------------------------

/**
 * 日付から状態を生成する．日付が未指定の場合は今日．
 * @param date - 日付
 * @returns 状態
 */
export const getStateFromDate = (
  applyUnits: { 1?: Master; 2?: Master; 0?: Master } = {},
  date: Date = new Date()
): DateChangerState => ({
  year: date.getFullYear(),
  month: date.getMonth() + 1,
  half:
    TypeUtils.isNonNil(applyUnits[0]) || TypeUtils.isNil(applyUnits[1])
      ? 0
      : date.getDate() <= (applyUnits[1].endDay || 15)
      ? 1
      : 2,
  week: getWeekOfMonth(date),
  day: date.getDate(),
});

/**
 * 日付切替器フック
 * @param settings - 設定
 * @returns
 */
export const useDateChangers = (settings?: DateChangerSetting) => {
  const [state, setState] = React.useState<DateChangerState>(
    settings?.initialState || getStateFromDate(settings?.applyUnits)
  );

  const handleChange = (
    nextState: DateChangerState,
    onChange: Consumer<DateChangerState>
  ) => {
    const thisMonth = state.month;
    const nextMonth = nextState.month;
    if (thisMonth !== nextMonth && !TypeUtils.isNil(settings?.onChangeMonth)) {
      settings?.onChangeMonth(nextState);
    }
    setState(nextState);
    onChange(nextState);
  };

  /**
   * 日切替
   * @returns
   */
  const DayChanger: React.FunctionComponent<Partial<DateChangerProps>> = ({
    onChangeLeft = () => null,
    onChangeRight = () => null,
    ...props
  }) => (
    <DateChanger
      isDisabledLeft={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.leftDeadend)
          ? false
          : state.year < settings.leftDeadend.year ||
            (state.year === settings?.leftDeadend.year &&
              (state.month < settings?.leftDeadend.month ||
                (state.month === settings?.leftDeadend.month &&
                  state.day <= settings?.leftDeadend.day)))
      }
      isDisabledRight={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.rightDeadend)
          ? false
          : settings?.rightDeadend.year < state.year ||
            (settings?.rightDeadend.year === state.year &&
              (settings?.rightDeadend.month < state.month ||
                (settings?.rightDeadend.month === state.month &&
                  settings?.rightDeadend.day <= state.day)))
      }
      onChangeLeft={() => {
        handleChange(
          getStateFromDate(
            props.applyUnits,
            new Date(state.year, state.month - 1, state.day - 1)
          ),
          onChangeLeft
        );
      }}
      onChangeRight={() => {
        handleChange(
          getStateFromDate(
            props.applyUnits,
            new Date(state.year, state.month - 1, state.day + 1)
          ),
          onChangeRight
        );
      }}
      state={state}
      value={`${state.year}/${state.month
        .toString()
        .padStart(2, '0')}/${state.day.toString().padStart(2, '0')}`}
      {...props}
    />
  );

  /**
   * 半月切替
   * @returns
   */
  const HalfMonthChanger: React.FunctionComponent<
    Partial<DateChangerProps> & Required<Pick<DateChangerProps, 'applyUnits'>>
  > = ({ onChangeLeft = () => null, onChangeRight = () => null, ...props }) => (
    <DateChanger
      isDisabledLeft={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.leftDeadend)
          ? false
          : state.year < settings.leftDeadend.year ||
            (state.year === settings.leftDeadend.year &&
              (state.month < settings.leftDeadend.month ||
                (state.month === settings.leftDeadend.month &&
                  state.half <= settings.leftDeadend.half)))
      }
      isDisabledRight={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.rightDeadend)
          ? false
          : settings.rightDeadend.year < state.year ||
            (settings.rightDeadend.year === state.year &&
              (settings.rightDeadend.month < state.month ||
                (settings.rightDeadend.month === state.month &&
                  settings.rightDeadend.half <= state.half)))
      }
      onChangeLeft={() => {
        handleChange(
          state.half === 2
            ? {
                ...state,
                half: 1,
                week: 1,
                day: props.applyUnits[1]?.startDay || 1,
              }
            : state.month > 1
            ? getStateFromDate(
                props.applyUnits,
                new Date(
                  state.year,
                  state.month - 2,
                  props.applyUnits[2]?.startDay || 16
                )
              )
            : getStateFromDate(
                props.applyUnits,
                new Date(
                  state.year - 1,
                  11,
                  props.applyUnits[2]?.startDay || 16
                )
              ),
          onChangeLeft
        );
      }}
      onChangeRight={() => {
        handleChange(
          state.half === 1
            ? getStateFromDate(
                props.applyUnits,
                new Date(
                  state.year,
                  state.month - 1,
                  props.applyUnits[2]?.startDay || 16
                )
              )
            : state.month < 12
            ? {
                ...state,
                month: state.month + 1,
                half: 1,
                week: 1,
                day: props.applyUnits[1]?.startDay || 1,
              }
            : {
                year: state.year + 1,
                month: 1,
                half: 1,
                week: 1,
                day: props.applyUnits[1]?.startDay || 1,
              },
          onChangeRight
        );
      }}
      state={state}
      value={`${state.year}/${state.month.toString().padStart(2, '0')} (${
        state.half === 1 ? '前半' : '後半'
      })`}
      {...props}
    />
  );

  /**
   * 年月切替
   * @returns
   */
  const MonthChanger: React.FunctionComponent<Partial<DateChangerProps>> = ({
    onChangeLeft = () => null,
    onChangeRight = () => null,
    ...props
  }) => (
    <DateChanger
      isDisabledLeft={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.leftDeadend)
          ? false
          : state.year < settings.leftDeadend.year ||
            (state.year === settings.leftDeadend.year &&
              state.month <= settings.leftDeadend.month)
      }
      isDisabledRight={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.rightDeadend)
          ? false
          : settings.rightDeadend.year < state.year ||
            (settings.rightDeadend.year === state.year &&
              settings.rightDeadend.month <= state.month)
      }
      onChangeLeft={() => {
        handleChange(
          state.month > 1
            ? { ...state, month: state.month - 1, half: 1, week: 1, day: 1 }
            : { year: state.year - 1, month: 12, half: 1, week: 1, day: 1 },
          onChangeLeft
        );
      }}
      onChangeRight={() => {
        handleChange(
          state.month < 12
            ? { ...state, month: state.month + 1, half: 1, week: 1, day: 1 }
            : { year: state.year + 1, month: 1, half: 1, week: 1, day: 1 },
          onChangeRight
        );
      }}
      state={state}
      value={`${state.year}/${state.month.toString().padStart(2, '0')}`}
      {...props}
    />
  );

  /**
   * 週切替 (月毎)
   * @returns
   */
  const WeekChanger: React.FunctionComponent<Partial<DateChangerProps>> = ({
    onChangeLeft = () => null,
    onChangeRight = () => null,
    ...props
  }) => (
    <DateChanger
      isDisabledLeft={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.leftDeadend)
          ? false
          : state.year < settings.leftDeadend.year ||
            (state.year === settings.leftDeadend.year &&
              (state.month < settings.leftDeadend.month ||
                (state.month === settings.leftDeadend.month &&
                  state.week <= settings.leftDeadend.week)))
      }
      isDisabledRight={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.rightDeadend)
          ? false
          : settings.rightDeadend.year < state.year ||
            (settings.rightDeadend.year === state.year &&
              (settings.rightDeadend.month < state.month ||
                (settings.rightDeadend.month === state.month &&
                  settings.rightDeadend.week <= state.week)))
      }
      onChangeLeft={() => {
        if (state.week > 1) {
          // 初週以外
          if (state.day < 8) {
            // 1週前が前月 --> 当月の朔
            handleChange({ ...state, week: 1, day: 1 }, onChangeLeft);
          } else {
            // --> 前週の初日
            handleChange(
              getStateFromDate(
                props.applyUnits,
                startOfWeek(
                  new Date(state.year, state.month - 1, state.day - 7)
                )
              ),
              onChangeLeft
            );
          }
        } else if (state.month > 1) {
          // 2月以降の初週 --> 前月の最終週の初日
          handleChange(
            getStateFromDate(
              props.applyUnits,
              startOfWeek(
                lastDayOfMonth(new Date(state.year, state.month - 2, 1))
              )
            ),
            onChangeLeft
          );
        } else {
          // 1月の初週 --> 前年の最終週の初日
          handleChange(
            getStateFromDate(
              props.applyUnits,
              startOfWeek(lastDayOfYear(new Date(state.year - 1, 1, 1)))
            ),
            onChangeLeft
          );
        }
      }}
      onChangeRight={() => {
        if (
          state.week < getWeeksInMonth(new Date(state.year, state.month - 1, 1))
        ) {
          // 最終週ではない --> 次週の初日
          handleChange(
            getStateFromDate(
              props.applyUnits,
              startOfWeek(
                new Date(state.year, state.month - 1, 7 * state.week + 1)
              )
            ),
            onChangeRight
          );
        } else if (state.month < 12) {
          // 12月以外の最終週 --> 翌月の朔
          handleChange(
            {
              year: state.year,
              month: state.month + 1,
              half: 1,
              week: 1,
              day: 1,
            },
            onChangeRight
          );
        } else {
          // 12月の最終週 --> 翌年の元日
          handleChange(
            {
              year: state.year + 1,
              month: 1,
              half: 1,
              week: 1,
              day: 1,
            },
            onChangeRight
          );
        }
      }}
      state={state}
      value={`${state.year}/${state.month.toString().padStart(2, '0')} 第${
        state.week
      }週`}
      {...props}
    />
  );

  /**
   * 年切替
   * @returns
   */
  const YearChanger: React.FunctionComponent<Partial<DateChangerProps>> = ({
    onChangeLeft = () => null,
    onChangeRight = () => null,
    ...props
  }) => (
    <DateChanger
      isDisabledLeft={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.leftDeadend)
          ? false
          : state.year <= settings.leftDeadend.year
      }
      isDisabledRight={
        TypeUtils.isNil(settings) || TypeUtils.isNil(settings.rightDeadend)
          ? false
          : settings.rightDeadend.year <= state.year
      }
      onChangeLeft={() => {
        // --> 前年の元日
        handleChange(
          {
            year: state.year - 1,
            month: 1,
            half: 1,
            week: 1,
            day: 1,
          },
          onChangeLeft
        );
      }}
      onChangeRight={() => {
        // --> 翌年の元日
        handleChange(
          {
            year: state.year + 1,
            month: 1,
            half: 1,
            week: 1,
            day: 1,
          },
          onChangeRight
        );
      }}
      state={state}
      value={state.year}
      {...props}
    />
  );

  return {
    DayChanger,
    HalfMonthChanger,
    MonthChanger,
    WeekChanger,
    YearChanger,
    setState,
    state,
  };
};

export const generateInitialState = (
  obj: URLSearchParams,
  required: Array<keyof DateChangerState>,
  optional?: Array<keyof DateChangerState>
) => {
  const initialState: Partial<DateChangerState> = {};

  for (const property of required) {
    const value = obj.get(property);
    if (TypeUtils.isNil(value)) {
      return undefined;
    }
    const int = parseInt(value);
    if (Number.isNaN(int)) {
      return undefined;
    }
    initialState[property] = int;
  }

  optional
    ?.map(
      (property) =>
        [property, obj.get(property)] as [keyof DateChangerState, string]
    )
    .filter(([property, value]) => !TypeUtils.isNil(value))
    .map(
      ([property, value]) =>
        [property, parseInt(value!)] as [keyof DateChangerState, number]
    )
    .filter(([property, int]) => !Number.isNaN(int))
    .forEach(([property, int]) => {
      initialState[property] = int;
    });

  return initialState as DateChangerState;
};
